import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { 
  Shield, Truck, Calendar, Loader2, Save, Users, Check, X,
  Search, CheckCircle, XCircle, RefreshCw, Info, Link, UserPlus
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

export default function GestionDroitsHECPage() {
  const { user } = useAuth();
  const token = localStorage.getItem("token");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [plannings, setPlannings] = useState([]);
  const [selectedPlanningId, setSelectedPlanningId] = useState(null);
  const [accessRights, setAccessRights] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [hasChanges, setHasChanges] = useState(false);
  const [localChanges, setLocalChanges] = useState({});
  
  // Link user dialog
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkingFt, setLinkingFt] = useState(null);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUserEmail, setSelectedUserEmail] = useState("");
  const [linkingInProgress, setLinkingInProgress] = useState(false);

  // Load all HEC plannings
  useEffect(() => {
    loadPlannings();
  }, []);

  // Load access rights when planning is selected
  useEffect(() => {
    if (selectedPlanningId) {
      loadAccessRights(selectedPlanningId);
    }
  }, [selectedPlanningId]);

  const loadPlannings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/hec/plannings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlannings(response.data);
      
      // Auto-select first planning if available
      if (response.data.length > 0 && !selectedPlanningId) {
        setSelectedPlanningId(response.data[0].id);
      }
    } catch (error) {
      console.error("Error loading plannings:", error);
      toast.error("Erreur lors du chargement des plannings");
    } finally {
      setLoading(false);
    }
  };

  const loadAccessRights = async (planningId) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}/hec/plannings/${planningId}/access-rights`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAccessRights(response.data);
      setLocalChanges({});
      setHasChanges(false);
    } catch (error) {
      console.error("Error loading access rights:", error);
      toast.error("Erreur lors du chargement des droits d'accès");
    } finally {
      setLoading(false);
    }
  };

  const toggleAccess = (index, currentValue) => {
    const newValue = !currentValue;
    setLocalChanges(prev => ({
      ...prev,
      [index]: newValue
    }));
    setHasChanges(true);
  };

  const getEffectiveValue = (ft) => {
    if (localChanges.hasOwnProperty(ft.index)) {
      return localChanges[ft.index];
    }
    return ft.hec_access_enabled;
  };

  const saveChanges = async () => {
    if (!selectedPlanningId || Object.keys(localChanges).length === 0) return;
    
    try {
      setSaving(true);
      
      // Prepare bulk updates
      const updates = Object.entries(localChanges).map(([index, enabled]) => ({
        index: parseInt(index),
        enabled
      }));
      
      await axios.put(
        `${API_URL}/hec/plannings/${selectedPlanningId}/access-rights/bulk`,
        updates,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`${updates.length} droit(s) d'accès mis à jour`);
      
      // Reload to get fresh data
      await loadAccessRights(selectedPlanningId);
    } catch (error) {
      console.error("Error saving access rights:", error);
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const enableAll = () => {
    if (!accessRights) return;
    const newChanges = {};
    accessRights.food_trucks.forEach(ft => {
      newChanges[ft.index] = true;
    });
    setLocalChanges(newChanges);
    setHasChanges(true);
  };

  const disableAll = () => {
    if (!accessRights) return;
    const newChanges = {};
    accessRights.food_trucks.forEach(ft => {
      newChanges[ft.index] = false;
    });
    setLocalChanges(newChanges);
    setHasChanges(true);
  };

  const cancelChanges = () => {
    setLocalChanges({});
    setHasChanges(false);
  };

  // Open link user dialog
  const openLinkDialog = async (ft) => {
    setLinkingFt(ft);
    setSelectedUserEmail("");
    setShowLinkDialog(true);
    
    // Load available users
    try {
      const response = await axios.get(
        `${API_URL}/hec/plannings/${selectedPlanningId}/users-for-linking`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAvailableUsers(response.data);
    } catch (error) {
      console.error("Error loading users:", error);
      toast.error("Erreur lors du chargement des utilisateurs");
    }
  };

  // Link user to food truck
  const linkUserToFt = async () => {
    if (!selectedUserEmail || !linkingFt || !selectedPlanningId) return;
    
    try {
      setLinkingInProgress(true);
      await axios.put(
        `${API_URL}/hec/plannings/${selectedPlanningId}/link-user/${linkingFt.index}?user_email=${encodeURIComponent(selectedUserEmail)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`${linkingFt.nom} lié à ${selectedUserEmail}`);
      setShowLinkDialog(false);
      
      // Reload access rights
      await loadAccessRights(selectedPlanningId);
    } catch (error) {
      console.error("Error linking user:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la liaison");
    } finally {
      setLinkingInProgress(false);
    }
  };

  // Filter food trucks by search term
  const filteredFoodTrucks = accessRights?.food_trucks?.filter(ft => 
    ft.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ft.specialite?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ft.responsable?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  // Count enabled (considering local changes)
  const enabledCount = accessRights?.food_trucks?.filter(ft => getEffectiveValue(ft)).length || 0;

  if (loading && !accessRights) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <span className="ml-2">Chargement...</span>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-heading font-bold text-secondary flex items-center gap-2">
            <Shield className="w-6 h-6 text-primary" />
            Gestion des Droits HEC
          </h1>
          <p className="text-muted-foreground">
            Activez l'accès au module "Mon Info HEC" pour chaque Food Truck
          </p>
        </div>
        
        {/* Save Button */}
        {hasChanges && (
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={cancelChanges}>
              Annuler
            </Button>
            <Button onClick={saveChanges} disabled={saving}>
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Sauvegarder ({Object.keys(localChanges).length} modification{Object.keys(localChanges).length > 1 ? 's' : ''})
            </Button>
          </div>
        )}
      </div>

      {/* Planning Selector */}
      <Card className="mb-6 border-[#E5E0DC]">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Calendar className="w-4 h-4 text-primary" />
            Sélectionner un Planning HEC
          </CardTitle>
        </CardHeader>
        <CardContent>
          {plannings.length === 0 ? (
            <p className="text-muted-foreground">Aucun planning HEC disponible</p>
          ) : (
            <Select value={selectedPlanningId || ""} onValueChange={setSelectedPlanningId}>
              <SelectTrigger className="w-full max-w-md">
                <SelectValue placeholder="Sélectionner un planning..." />
              </SelectTrigger>
              <SelectContent>
                {plannings.map((planning) => (
                  <SelectItem key={planning.id} value={planning.id}>
                    {planning.nom_evenement} ({planning.date_debut} - {planning.date_fin})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </CardContent>
      </Card>

      {/* Access Rights Management */}
      {accessRights && (
        <Card className="border-[#E5E0DC]">
          <CardHeader className="bg-[#F9F7F5] border-b border-[#E5E0DC]">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="w-5 h-5 text-primary" />
                  {accessRights.planning_nom}
                </CardTitle>
                <CardDescription className="mt-1">
                  <span className="font-semibold text-green-600">{enabledCount}</span> / {accessRights.total_count} Food Trucks avec accès activé
                </CardDescription>
              </div>
              
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={enableAll}>
                  <CheckCircle className="w-4 h-4 mr-1 text-green-600" />
                  Tout activer
                </Button>
                <Button variant="outline" size="sm" onClick={disableAll}>
                  <XCircle className="w-4 h-4 mr-1 text-red-600" />
                  Tout désactiver
                </Button>
                <Button variant="ghost" size="sm" onClick={() => loadAccessRights(selectedPlanningId)}>
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            {/* Search */}
            <div className="relative mt-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input 
                placeholder="Rechercher un Food Truck..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 max-w-md"
              />
            </div>
          </CardHeader>
          
          <CardContent className="p-0">
            {/* Info Banner */}
            <div className="px-4 py-3 bg-blue-50 border-b border-blue-100 flex items-center gap-2">
              <Info className="w-4 h-4 text-blue-600" />
              <span className="text-sm text-blue-700">
                Les Food Trucks avec accès activé pourront modifier leurs informations et menus via le module "Mon Info HEC"
              </span>
            </div>
            
            {/* Food Trucks List */}
            <div className="divide-y divide-gray-100">
              {filteredFoodTrucks.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  <Truck className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Aucun Food Truck trouvé</p>
                </div>
              ) : (
                filteredFoodTrucks.map((ft) => {
                  const isEnabled = getEffectiveValue(ft);
                  const hasLocalChange = localChanges.hasOwnProperty(ft.index);
                  
                  return (
                    <div 
                      key={ft.index}
                      className={`flex items-center justify-between p-4 hover:bg-gray-50 transition-colors ${
                        hasLocalChange ? 'bg-amber-50' : ''
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        {/* Checkbox */}
                        <Checkbox
                          id={`access-${ft.index}`}
                          checked={isEnabled}
                          onCheckedChange={() => toggleAccess(ft.index, isEnabled)}
                          className="h-5 w-5"
                        />
                        
                        {/* FT Info */}
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            isEnabled ? 'bg-green-100' : 'bg-gray-100'
                          }`}>
                            <Truck className={`w-5 h-5 ${isEnabled ? 'text-green-600' : 'text-gray-400'}`} />
                          </div>
                          <div>
                            <p className="font-medium text-secondary">{ft.nom}</p>
                            <p className="text-sm text-muted-foreground">
                              {ft.specialite || "Spécialité non définie"}
                              {ft.responsable && ` • ${ft.responsable}`}
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Status Badge */}
                      <div className="flex items-center gap-2">
                        {/* Link button */}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openLinkDialog(ft)}
                          className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                          title="Lier à un utilisateur"
                        >
                          <Link className="w-4 h-4" />
                        </Button>
                        
                        {/* Linked email indicator */}
                        {ft.adherent_id ? (
                          <Badge variant="outline" className="border-blue-300 text-blue-700 bg-blue-50 text-xs">
                            <UserPlus className="w-3 h-3 mr-1" />
                            Lié
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="border-orange-300 text-orange-600 bg-orange-50 text-xs">
                            Non lié
                          </Badge>
                        )}
                        
                        {hasLocalChange && (
                          <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50">
                            Modifié
                          </Badge>
                        )}
                        <Badge 
                          className={`${
                            isEnabled 
                              ? 'bg-green-100 text-green-700 border-green-200' 
                              : 'bg-gray-100 text-gray-500 border-gray-200'
                          }`}
                        >
                          {isEnabled ? (
                            <>
                              <Check className="w-3 h-3 mr-1" />
                              Accès activé
                            </>
                          ) : (
                            <>
                              <X className="w-3 h-3 mr-1" />
                              Accès désactivé
                            </>
                          )}
                        </Badge>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Link User Dialog */}
      <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link className="w-5 h-5 text-primary" />
              Lier "{linkingFt?.nom}" à un utilisateur
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Sélectionnez l'utilisateur qui pourra modifier les informations de ce Food Truck via "Mon Info HEC".
            </p>
            
            <Select value={selectedUserEmail} onValueChange={setSelectedUserEmail}>
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner un utilisateur..." />
              </SelectTrigger>
              <SelectContent>
                {availableUsers.map((u) => (
                  <SelectItem key={u.id} value={u.email}>
                    <div className="flex items-center gap-2">
                      <span>{u.email}</span>
                      {u.food_truck_name && (
                        <Badge variant="outline" className="text-xs">{u.food_truck_name}</Badge>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {selectedUserEmail && (
              <div className="p-3 bg-blue-50 rounded-lg text-sm">
                <p className="text-blue-800">
                  L'utilisateur <strong>{selectedUserEmail}</strong> pourra modifier les informations de <strong>{linkingFt?.nom}</strong> via le module "Mon Info HEC".
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLinkDialog(false)}>
              Annuler
            </Button>
            <Button onClick={linkUserToFt} disabled={!selectedUserEmail || linkingInProgress}>
              {linkingInProgress ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Link className="w-4 h-4 mr-2" />
              )}
              Lier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
